'use client'

import * as React from 'react'
import { Loader2, Search } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Alert } from '@/components/ui/alert'
import { Field } from '@/components/encuentro/ui/field'
import {
  BrandPreview,
  ColourField,
  LogoPicker,
  SelectField,
} from '@/components/encuentro/screens/platform-admin/institution-wizard/review-parts'
import { SlugField } from '@/components/encuentro/screens/school-signup/slug-field'
import type { StartFormState } from '@/hooks/use-start-form'
import type { StartLookupAction } from '@/lib/start/public-lookup'
import {
  startInstitutionProfileSchema,
  type StartInstitutionProfile,
} from '@/lib/schemas/start-institution-profile'
import type { LookupResult } from '@/lib/institution-lookup/types'
import { isValidLocale, locales } from '@/lib/i18n/config'
import { INSTITUTION_KINDS } from '@/lib/org/institution-noun'
import { ALUMNI_KINDS } from '@/lib/org/alumni-noun'
import { SHORT_NAME_MAX } from '@/lib/org/short-name'

export function emptyStartProfile(locale: string): StartInstitutionProfile {
  return {
    shortName: null,
    color: null,
    language: isValidLocale(locale) ? locale : 'en',
    timezone: null,
    institutionType: null,
    alumniWord: null,
    classYearTerm: null,
    logoPreview: null,
  }
}

export function StartInstitutionReview({
  form,
  analyze,
  profile,
  onProfile,
  onContinue,
  timezones,
}: {
  form: StartFormState
  analyze: StartLookupAction
  profile: StartInstitutionProfile | null
  onProfile: (profile: StartInstitutionProfile) => void
  onContinue: () => void
  timezones: readonly string[]
}) {
  const t = useTranslations('encuentro.schoolSignup.start.wizard')
  const words = useTranslations('encuentro.institutionWizard')
  const locale = useLocale()
  const [url, setUrl] = React.useState('')
  const [stage, setStage] = React.useState<'website' | 'review'>(profile ? 'review' : 'website')
  const [result, setResult] = React.useState<LookupResult | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [invalid, setInvalid] = React.useState(false)
  const inFlight = React.useRef(false)
  const draft = profile ?? emptyStartProfile(locale)
  const set = (patch: Partial<StartInstitutionProfile>) => onProfile({ ...draft, ...patch })

  const openManual = () => {
    onProfile(draft)
    setError(null)
    setStage('review')
  }
  const lookup = async () => {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      const response = await analyze(url)
      if (!response.ok) {
        setError(t(`errors.${response.error}`))
        return
      }
      setResult(response.result)
      const s = response.result.suggestions
      if (s.name) form.handleNameChange(s.name.value.slice(0, 80))
      if (s.slug) form.handleSlugChange(s.slug.value)
      onProfile({
        shortName: s.shortName?.value ?? null,
        color: s.color?.value ?? null,
        language: s.language?.value ?? draft.language,
        timezone: s.timezone?.value ?? null,
        institutionType: s.institutionType?.value ?? null,
        alumniWord: s.alumniWord?.value ?? null,
        classYearTerm: s.classYearTerm?.value ?? null,
        logoPreview: s.logos[0]?.preview ?? null,
      })
      setStage('review')
    } catch {
      setError(t('errors.failed'))
    } finally {
      setBusy(false)
      inFlight.current = false
    }
  }
  const continueReview = (event: React.FormEvent) => {
    event.preventDefault()
    const parsed = startInstitutionProfileSchema.safeParse(draft)
    const identity = z
      .object({ name: z.string().trim().min(2).max(80), slug: z.string().min(3) })
      .safeParse({ name: form.name, slug: form.slug })
    if (!parsed.success || !identity.success) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    onProfile(parsed.data)
    onContinue()
  }

  if (stage === 'website')
    return (
      <form
        data-testid="start-website"
        className="grid gap-5"
        onSubmit={(event) => {
          event.preventDefault()
          void lookup()
        }}
      >
        <h2 className="text-en-title">{t('websiteTitle')}</h2>
        <p className="text-en-body text-en-ink-muted">{t('websiteHelp')}</p>
        <Field
          id="institutionWebsite"
          label={t('website')}
          placeholder="institucion.edu"
          value={url}
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          onChange={(event) => setUrl(event.target.value)}
          maxLength={2048}
          disabled={busy}
        />
        {error && <Alert look="encuentro" tone="danger" body={error} />}
        <Button look="encuentro" variant="primary" type="submit" disabled={busy || !url.trim()}>
          {busy ? (
            <Loader2 aria-hidden className="animate-spin motion-reduce:animate-none" />
          ) : (
            <Search aria-hidden />
          )}
          {busy ? t('analyzing') : t('analyze')}
        </Button>
        {busy && (
          <p role="status" className="text-en-meta text-en-ink-muted">
            {t('analyzingHelp')}
          </p>
        )}
        <Button look="encuentro" variant="ghost" type="button" onClick={openManual} disabled={busy}>
          {t('manual')}
        </Button>
      </form>
    )

  return (
    <form data-testid="start-institution-review" className="grid gap-5" onSubmit={continueReview}>
      <h2 className="text-en-title">{t('reviewTitle')}</h2>
      <p className="text-en-body text-en-ink-muted">{t('reviewHelp')}</p>
      {result?.notes.some((note) => note !== 'ai_unavailable') && (
        <Alert look="encuentro" tone="warning" body={t('partial')} />
      )}
      <Field
        id="name"
        name="name"
        label={t('name')}
        value={form.name}
        onChange={(event) => form.handleNameChange(event.target.value)}
        maxLength={80}
        required
        autoComplete="organization"
      />
      <Field
        label={t('shortName')}
        value={draft.shortName ?? ''}
        onChange={(event) => set({ shortName: event.target.value || null })}
        maxLength={SHORT_NAME_MAX}
      />
      <SlugField
        label={t('slug')}
        prefix="alumnia.net/in/"
        id="slug"
        name="slug"
        value={form.slug}
        onChange={(event) => form.handleSlugChange(event.target.value)}
        required
        maxLength={40}
        status={{
          tone:
            form.slugStatus === 'taken' || form.slugStatus === 'reserved'
              ? 'danger'
              : form.slugStatus === 'available'
                ? 'positive'
                : 'muted',
          text: t(`slugStatus.${form.slugStatus}`),
        }}
      />
      {result && (
        <LogoPicker
          logos={result.suggestions.logos}
          value={
            draft.logoPreview
              ? result.suggestions.logos.findIndex((logo) => logo.preview === draft.logoPreview)
              : null
          }
          onChange={(index) =>
            set({
              logoPreview:
                index === null ? null : (result.suggestions.logos[index]?.preview ?? null),
            })
          }
        />
      )}
      {!result && draft.logoPreview && (
        <Button
          look="encuentro"
          variant="secondary"
          type="button"
          onClick={() => set({ logoPreview: null })}
        >
          {words('identity.logoNone')}
        </Button>
      )}
      <ColourField value={draft.color ?? ''} onChange={(color) => set({ color: color || null })} />
      <SelectField
        label={t('language')}
        value={draft.language}
        onChange={(value) => {
          if (isValidLocale(value)) set({ language: value })
        }}
        options={locales.map((value) => ({
          value,
          label: new Intl.DisplayNames([locale], { type: 'language' }).of(value) ?? value,
        }))}
      />
      <SelectField
        label={t('institutionType')}
        value={draft.institutionType ?? ''}
        onChange={(value) => {
          const parsed = startInstitutionProfileSchema.shape.institutionType.safeParse(value)
          if (parsed.success) set({ institutionType: parsed.data })
        }}
        options={[
          { value: '', label: t('defaultWords') },
          ...INSTITUTION_KINDS.map((value) => ({ value, label: words(`words.types.${value}`) })),
        ]}
      />
      <SelectField
        label={t('alumniWord')}
        value={draft.alumniWord ?? ''}
        onChange={(value) => {
          const parsed = startInstitutionProfileSchema.shape.alumniWord.safeParse(value)
          if (parsed.success) set({ alumniWord: parsed.data })
        }}
        options={[
          { value: '', label: t('defaultWords') },
          ...ALUMNI_KINDS.map((value) => ({ value, label: words(`words.alumniKinds.${value}`) })),
        ]}
      />
      <Field
        label={t('classYearTerm')}
        value={draft.classYearTerm ?? ''}
        onChange={(event) => set({ classYearTerm: event.target.value || null })}
        maxLength={60}
      />
      <SelectField
        label={t('timezone')}
        value={draft.timezone ?? ''}
        onChange={(value) => set({ timezone: value || null })}
        options={[
          { value: '', label: t('defaultTimezone') },
          ...Array.from(new Set(['UTC', ...timezones, ...(draft.timezone ? [draft.timezone] : [])]))
            .sort()
            .map((value) => ({ value, label: value.replaceAll('_', ' ').replaceAll('/', ' / ') })),
        ]}
      />
      <BrandPreview
        name={form.name}
        shortName={draft.shortName ?? ''}
        logo={draft.logoPreview}
        colour={draft.color ?? ''}
      />
      {invalid && <Alert look="encuentro" tone="danger" body={t('invalid')} />}
      <Button
        look="encuentro"
        variant="primary"
        type="submit"
        disabled={
          form.slugStatus === 'taken' ||
          form.slugStatus === 'reserved' ||
          form.slugStatus === 'checking'
        }
      >
        {t('continue')}
      </Button>
      <Button look="encuentro" variant="ghost" type="button" onClick={() => setStage('website')}>
        {t('changeWebsite')}
      </Button>
    </form>
  )
}
